import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock, FaUser, FaEye, FaEyeSlash, FaArrowLeft, FaLaptopCode } from 'react-icons/fa';
import { getApiUrl, storeSession, clearSession, verifySession } from './adminApi';
import './App.css';
const generateColorPalette = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return {
    r, g, b,
    light: `rgb(${Math.min(255, r + 30)}, ${Math.min(255, g + 30)}, ${Math.min(255, b + 30)})`,
    dark: `rgb(${Math.max(0, r - 30)}, ${Math.max(0, g - 30)}, ${Math.max(0, b - 30)})`
  };
};

const applyThemeAndColor = (isDark, accentColor) => {
  const root = document.documentElement;
  const palette = generateColorPalette(accentColor);
  
  root.setAttribute('data-theme', isDark ? 'dark' : 'light');
  root.style.setProperty('--accent-color', accentColor);
  root.style.setProperty('--accent-light', palette.light);
  root.style.setProperty('--accent-dark', palette.dark);
  root.style.setProperty('--accent-color-rgb', `${palette.r}, ${palette.g}, ${palette.b}`);
};

function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const savedColor = localStorage.getItem('accentColor');
    const isDark = savedTheme ? savedTheme === 'dark' : true;
    const accentColor = savedColor || '#3b82f6';
    
    applyThemeAndColor(isDark, accentColor);
    
    setTimeout(() => {
      setIsPageVisible(true);
    }, 100);
  }, []);

  // Si une session serveur est encore valide, on évite de redemander le mot de passe.
  useEffect(() => {
    let cancelled = false;

    verifySession().then((isAuthenticated) => {
      if (!cancelled && isAuthenticated) {
        navigate('/admin');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Toute session résiduelle est purgée avant d'en demander une nouvelle.
    clearSession();

    try {
      const response = await fetch(getApiUrl('/auth.php'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password
        })
      }).catch(() => {
        throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success && data.token) {
        storeSession(data.token, data.username);
        setIsLoading(false);
        navigate('/admin');
        return;
      }

      setIsLoading(false);

      // Le verrou anti-force-brute est appliqué par le serveur : le message
      // vient de lui, il ne peut plus être contourné depuis le navigateur.
      if (response.status === 429) {
        setError(data.message || 'Trop de tentatives. Réessayez dans quelques minutes.');
        return;
      }

      setError(data.message || 'Nom d\'utilisateur ou mot de passe incorrect');
    } catch (err) {
      setIsLoading(false);
      setError(err.message || 'Erreur de connexion au serveur. Vérifiez votre connexion internet.');
    }
  };

  return (
    <div className="app">
      <div className={`login-container ${isPageVisible ? 'login-visible' : ''}`}>
        <div className="login-background">
          <div className="login-orb orb-1"></div>
          <div className="login-orb orb-2"></div>
          <div className="login-orb orb-3"></div>
        </div>

        <div className="login-card">
          <button 
            className="login-back-btn"
            onClick={() => navigate('/')}
          >
            <FaArrowLeft />
            <span>Retour</span>
          </button>

          <div className="login-header">
            <div className="login-icon-container">
              <div className="login-icon-ring"></div>
              <div className="login-icon">
                <FaLaptopCode />
              </div>
            </div>
            <h1 className="login-title">Accès Administrateur</h1>
            <p className="login-subtitle">Connectez-vous pour gérer votre portfolio</p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-input-group">
              <label htmlFor="username">
                <FaUser />
                <span>Nom d'utilisateur</span>
              </label>
              <div className="login-input-wrapper">
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Entrez votre nom d'utilisateur"
                  required
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="login-input-group">
              <label htmlFor="password">
                <FaLock />
                <span>Mot de passe</span>
              </label>
              <div className="login-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez votre mot de passe"
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {error && (
              <div className="login-error">
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className={`login-submit-btn ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="login-spinner"></span>
                  <span>Connexion en cours...</span>
                </>
              ) : (
                <>
                  <FaLock />
                  <span>Se connecter</span>
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>Portfolio Mrtchouk © {new Date().getFullYear()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;

